import React from 'react';
import styled from 'styled-components';
import { useProjectListActions } from '../../shared/hooks/useApi/useProjectList';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import searchIcon from '../../asset/icon/see.png';
import searchNotIcon from '../../asset/icon/see_not.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import { useEditMode } from '../../shared/hooks/useEditMode';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';

interface ProjectEditControllerProps {
  projectId: string;
  isPublic: boolean;
}

const ProjectEditController: React.FC<ProjectEditControllerProps> = ({
  projectId,
  isPublic
}) => {
  const { toggleProjectAccess, deleteProject } = useProjectListActions();
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const { isEditMode } = useEditMode();

  const handleDelete = async () => {
    const callback = async () => {
      await deleteProject({ projectId });
    };
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_PROJECT'),
      callBack: callback
    });
  };

  const handleToggleAccess = async (value: boolean) => {
    await toggleProjectAccess({
      id: projectId,
      isPublic: value
    });
  };

  const handleToggleAccessWithConfirm = async (value: boolean) => {
    const message = value
      ? getAlertMessage('CONFIRM_MAKE_PROJECT_PUBLIC')
      : getAlertMessage('CONFIRM_MAKE_PROJECT_PRIVATE');

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: message,
      callBack: () => handleToggleAccess(value)
    });
  };

  return (
    <>
    {isEditMode &&
    <TransparentLayer>
      <BtnContainer>
        <UniversalIconBtn 
          size="fit" 
          border={true} 
          backgroundImg={isPublic ? searchIcon : searchNotIcon} 
          onClick={() => handleToggleAccessWithConfirm(!isPublic)} />
        <UniversalIconBtn 
          size="fit" 
          border={true} 
          backgroundImg={deleteCloseIcon} 
          backgroundImgHover={deleteOpenIcon}
          onClick={handleDelete} />
      </BtnContainer>
    </TransparentLayer>
    }
    {(isEditMode || (!isEditMode && !isPublic)) &&
      <Indicator>
        {isPublic ? ":Public" : ":Private"}
      </Indicator>
    }
    </>
  );
};

const TransparentLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  background: linear-gradient(to right, transparent 50%, ${({ theme }) => theme.colors.color_Gray_06} 150%);
  backdrop-filter: blur(0px);
  &:hover {
    opacity: 1;
    backdrop-filter: blur(2px);
  }
  transition: all 0.6s ease-in-out;
  cursor: default;
`;


const BtnContainer = styled.div`
  position: absolute;
  right: clamp(10px, 1vw, 15px);
  top: clamp(10px, 1vw, 15px);
  width: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
`;

const Indicator = styled.div`
  position: absolute;
  right: -4px;
  top: 0;
  width: 0;
  height: 0;
  ${({ theme }) => theme.typography.Body_03_1};
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

export default ProjectEditController;
